import React from 'react';

import {assert} from 'Asserts';
import {
  ProcedureType,
  ProcessClientOrigin,
  ProcessExecutionType,
  ProcessFlowchartNodeDetailsType,
  ProcessSingletonTaskSchemaName,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import DocumentActions from 'notebook/documents/DocumentActions';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  composeProcedureTypeMetadata,
  PROCEDURE_SAVE_STARTED_EVENT,
} from 'performance/processProceduresEvents';
import mergeIdsIntoPowProcedureFlowchartConfig from 'procedures/network/mergeIdsIntoPowProcedureFlowchart';
import {getDocumentVersionUpdatesFromFlowchartConfig} from 'procedures/procedurePagePersistenceUtils';
import {TaskDefinitionVersionSpec} from 'processes/dao/ProcessFlowchartConfigDAO';
import {
  FlowchartConfigWithMetadataAndRootTaskSchema,
  ProcessFlowchartAdapter,
  ProcessFlowchartAdditionalPersistedData,
  RemoveNodeConfirmationContent,
  RemoveNodeConfirmationParams,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/ProcessFlowchartAdapter';
import {NodeCreationContext} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/ProcessFlowchartNodeAdapter';
import {isTaskNodeDetailsGQL} from 'processes/graph-builder-processes/flowchart-viz/util/getNodeDetailsTypeForGQL';
import {clientOriginToProcedureType} from 'processes/ProcessConstants';
import {
  STANDARD_END_NODE_ICON,
  STANDARD_END_NODE_NAME,
  STANDARD_ROOT_NODE_ICON,
  STANDARD_ROOT_NODE_NAME,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {ProcessTaskDefinitionVersionGraphQL} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {
  TaskDefinitionVersionSpecWithTaskSchema,
  TaskSchemaNodeSectionData,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import {ProcessFlowchartConfigForEditorWithLatestVersionGraphQL} from '../../../graphql-fragments/processes/__generated/ProcessFlowchartConfigForEditorWithLatestVersionGraphQL';
import {ProcessTaskSchemaGraphQL} from '../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from '../ProceduresStringKeys';
import {StepIdToDocumentIdAndVersionIdMap, ValuableProcedureServerData} from './ProcedureFlowchartTypes';

/** Exported only for testing */
export function createIdForAmountField(materialInputVersionId: string): string {
  // The special index lets us store two fields for the same material input version
  return `${materialInputVersionId}-amt`;
}

/** @package */
export function extractValuableProcedureDataFromServerResponse(
  serverResponse: ProcessFlowchartConfigForEditorWithLatestVersionGraphQL
): ValuableProcedureServerData {
  const allNodes = (serverResponse.flowchartConfigVersion?.templateFlowchart?.nodeConfigs ?? [])
    .map((node) =>
      isTaskNodeDetailsGQL(node.nodeDetails) ? node.nodeDetails.processTaskDefinitionVersion : null
    )
    .map((ptdv) =>
      ptdv &&
      !isInaccessible(ptdv) &&
      ptdv.applicationData?.__typename === 'ProcedureMethodDefinitionVersionNode'
        ? ptdv.applicationData
        : null
    )
    .filter((methodNode): methodNode is NonNullable<typeof methodNode> => methodNode != null);

  /**
   * We extract a mapping of step IDs to their associated document version IDs for all steps in the procedure. The
   * DVIDs are not otherwise present on the cid-id map, or elsewhere on the client or in the server response.
   *
   * These DVIDs need to be installed after a save to ensure we're up to date with the server changes.
   */
  const stepIdToDocumentIdAndVersionIdMap: StepIdToDocumentIdAndVersionIdMap = {};
  const allSteps = allNodes.map((methodNode) => methodNode.stepGroups.flatMap((sg) => sg.steps)).flat();
  for (const step of allSteps) {
    if (step.id != null) {
      // These values are merged into the client flowchart inside mergeIdsIntoProcedureStepConfig
      stepIdToDocumentIdAndVersionIdMap[step.id] = {
        // DocumentID is added when a new step is created by the server. Null values suppress updates.
        documentId: step.documentVersion?.document?.id ?? null,
        // Document /version/ ID is updated on every change to a step document.
        documentVersionId: step.documentVersion?.id ?? null,
      };
    }
  }

  /**
   * We extract a mapping of parameter version IDs to the parent parameter IDs.
   *
   * A newly created parameter on the client has a null ID and no CID, so it will not be present in the cid-id map.
   *
   * Parameter IDs need to be installed to correctly reuse parameter versions on the next save.
   */
  const parameterVersionIdToParameterIdMap: Record<string, string> = {};
  const assignParameterId = (versionId: string, parameterId: string): void => {
    assert(
      parameterVersionIdToParameterIdMap[versionId] == null,
      'Duplicate parameter version ID encountered'
    );
    parameterVersionIdToParameterIdMap[versionId] = parameterId;
  };
  for (const node of allNodes) {
    for (const {version} of node.dataParameterSettings) {
      assignParameterId(version.id, version.procedureDataParameterId);
    }
    for (const {version} of node.materialInputSettings) {
      assignParameterId(version.id, version.procedureMaterialInputId);
    }
    for (const {version} of node.materialOutputSettings) {
      assignParameterId(version.id, version.procedureMaterialOutputId);
    }
    for (const {version} of node.equipmentSettings) {
      assignParameterId(version.id, version.procedureEquipmentId);
    }
  }

  /**
   * We also need the REST schema fields for any parameters or materials that were created or updated during the save.
   */
  const parameterPlusSchemaFields: Record<string, SchemaField> = {};
  const assign = (id: string, restValueField: SchemaField): void => {
    assert(parameterPlusSchemaFields[id] == null, 'Duplicate parameter ID encountered');
    parameterPlusSchemaFields[id] = restValueField;
  };
  for (const node of allNodes) {
    for (const {version} of node.dataParameterSettings) {
      assign(version.id, version.restValueField);
    }
    for (const {version} of node.materialInputSettings) {
      assign(version.id, version.restMaterialField);
      assign(createIdForAmountField(version.id), version.restAmountField);
    }
    for (const {version} of node.materialOutputSettings) {
      assign(version.id, version.restMaterialField);
    }
    for (const {version} of node.equipmentSettings) {
      assign(version.id, version.restEquipmentField);
    }
  }

  return {stepIdToDocumentIdAndVersionIdMap, parameterVersionIdToParameterIdMap, parameterPlusSchemaFields};
}

export default class ProcedureRootNodeAdapter extends ProcessFlowchartAdapter<
  null,
  null,
  ValuableProcedureServerData
> {
  readonly executionType: ProcessExecutionType = ProcessExecutionType.PROCEDURE;
  readonly typeDisplayName: string = 'procedure';

  nodeDetailsSidepanelContent = [];

  convertTaskDefinitionVersionGQLToApplicationData(
    taskDefinitionVersionGQL: ProcessTaskDefinitionVersionGraphQL
  ): null {
    return null;
  }

  convertTaskDefinitionVersionSpecToNetworkApplicationData(
    taskDefinitionVersionSpec: TaskDefinitionVersionSpecWithTaskSchema<null>,
    flowchartConfigId: string
  ): null {
    return null;
  }

  getNodeName(
    taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema,
    isFlowchartOutput?: boolean
  ): string {
    if (isFlowchartOutput === true) {
      return STANDARD_END_NODE_NAME;
    } else {
      return STANDARD_ROOT_NODE_NAME;
    }
  }

  getNodeSubtitle(taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema): string | null {
    return null;
  }

  getNodeIcon(
    taskDefinitionVersion: TaskDefinitionVersionSpec,
    isFlowchartOutput?: boolean
  ): BenchlingIconName {
    if (isFlowchartOutput === true) {
      return STANDARD_END_NODE_ICON;
    } else {
      return STANDARD_ROOT_NODE_ICON;
    }
  }

  createSections(
    taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema,
    nodeDetailsType: ProcessFlowchartNodeDetailsType
  ): Array<TaskSchemaNodeSectionData> {
    return [];
  }

  onCreateNewNode(
    context: NodeCreationContext
  ): Promise<TaskDefinitionVersionSpecWithTaskSchema<null> | null> {
    assert(false, 'Procedure root nodes should not be created');
  }

  getNewNodeDetails(context: NodeCreationContext): {
    nodeName: string;
    nodeSubtitle: string | null;
    nodeIcon: BenchlingIconName | null;
  } {
    assert(false, 'Procedure root nodes should not be created');
  }

  getSingletonTaskSchemaName(
    rootTaskSchema: ProcessTaskSchemaGraphQL
  ): ProcessSingletonTaskSchemaName | null {
    return null;
  }

  onSaveStart(flowchartConfig: FlowchartConfigWithMetadataAndRootTaskSchema): void {
    const clientOrigin = flowchartConfig.metadata.rootTaskSchema.clientOrigin;
    let procedureType!: ProcedureType;
    if (clientOrigin === ProcessClientOrigin.RECIPES) {
      procedureType = ProcedureType.RECIPE;
    } else if (clientOrigin === ProcessClientOrigin.ASSAYS) {
      procedureType = ProcedureType.ASSAY;
    } else {
      assert(false, 'Unsupported client origin', {clientOrigin});
    }

    const eventMetadata = composeProcedureTypeMetadata(procedureType);
    logProfilerTimingEvent(PROCEDURE_SAVE_STARTED_EVENT, eventMetadata);

    const documentVersionUpdates = getDocumentVersionUpdatesFromFlowchartConfig(flowchartConfig);
    DocumentActions.notifySaveStart(documentVersionUpdates);
  }

  /**
   * After a successful save, extract valuable data from the server response, which has some properties
   * that are not available elsewhere. Whatever data we extract here will be passed to `onSaveComplete` below.
   *
   * @param serverResponse - The server response containing the latest flowchart config data.
   * @returns An object containing the mapping of step IDs to document version IDs.
   */
  override extractValuableDataFromServerResponse(
    serverResponse: ProcessFlowchartConfigForEditorWithLatestVersionGraphQL
  ): ValuableProcedureServerData {
    return extractValuableProcedureDataFromServerResponse(serverResponse);
  }
  onSaveComplete(
    persistedFlowchartConfig: FlowchartConfigWithMetadataAndRootTaskSchema,
    additionalPersistedData: ProcessFlowchartAdditionalPersistedData & {
      valuableServerData: ValuableProcedureServerData;
    }
  ): Promise<FlowchartConfigWithMetadataAndRootTaskSchema> {
    const documentVersionUpdates = getDocumentVersionUpdatesFromFlowchartConfig(persistedFlowchartConfig);
    DocumentActions.notifySaveComplete(documentVersionUpdates, additionalPersistedData.cidToIdMap);

    const result = mergeIdsIntoPowProcedureFlowchartConfig(
      persistedFlowchartConfig,
      additionalPersistedData.cidToIdMap,
      additionalPersistedData.valuableServerData
    );
    return Promise.resolve(result);
  }

  override isDeletable(): boolean {
    return false;
  }

  override getRemoveNodeConfirmationContent(
    params: RemoveNodeConfirmationParams
  ): RemoveNodeConfirmationContent {
    const clientOrigin = params.metadata.rootTaskSchema.clientOrigin;
    const procedureType = clientOriginToProcedureType[clientOrigin];
    assert(procedureType != null, 'Unexpected procedure type encountered');

    const procedureTypeName = getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
      stringCase: StringCase.LOWER,
    });
    const methodTypeName = getProcedureUIString(ProceduresStringKeys.METHOD, procedureType, {
      stringCase: StringCase.LOWER,
      plural: true,
    });

    const title = `Remove from ${procedureTypeName}?`;
    const subtitle = params.isMultiNodeRemoval ? (
      `This will permanently remove these ${methodTypeName} from the ${procedureTypeName}. All material mappings to or from them will also be removed.`
    ) : (
      <>
        This will permanently remove <strong>{params.nodeName}</strong> from the {procedureTypeName}. All
        material mappings to or from it will also be removed.
      </>
    );

    return {title, subtitle};
  }
}
